import last from "lodash/last";
import forEach from "lodash/forEach";
import sortBy from "lodash/sortBy";
import { getStateCamFromSocket } from "@/utils";
import sessionStorageKeys from "@/consts/sessionStorageKeys";
import helper from "@/common/helper";

import { MainView } from "@/classes/views/mainView";

const prepareCamStatusesIconList = cam => {
  const { focused, unblinded, state, valid = null, recorded } = cam.status;
  const { telemetry_id } = cam.settings;
  const alarmed = (cam.states && cam.states.alarmed) || null;

  return [
    ...(!!telemetry_id ? ["swivel"] : ["default"]),
    ...(recorded === "1" ? ["rec"] : []), // добавляется иконка записи
    ...(["DISARM_DETACHED", "DETACHED"].includes(state) ||
    (valid !== null && valid === false)
      ? ["detached"]
      : []),
    ...(focused === "0" ||
    unblinded === "0" ||
    state === "ALARMED" ||
    (alarmed !== null && alarmed)
      ? ["alarmed"]
      : [])
  ];
};

const prepareCam = cam => {
  let icon = prepareCamStatusesIconList(cam);
  return {
    ...cam,
    selected: false,
    icon: icon.join(" ")
  };
};

const getView = (state, viewId) => {
  return (
    state.localViews.find(view => view.id == viewId) ||
    (viewId === state.mainView.id ? state.mainView : null)
  );
};

let debounceLimit = 60;
let debounceCount = 0;
const CAMS_COUNT_TO_INCREASE_DEBOUNCE_LIMIT = 15;

let debounceTimeout = null;
const queueMap = new Map();

const addToMap = cam => {
  if (!queueMap.has(cam.id)) {
    queueMap.set(cam.id, cam);
    return;
  }

  const camInMap = queueMap.get(cam.id);

  queueMap.set(cam.id, {
    ...cam,
    states: {
      ...camInMap.states,
      ...cam.states
    },
    rt: {
      ...camInMap.rt,
      ...camInMap.status,
      ...cam.rt
    }
  });
};

export default {
  setUser(state, user) {
    state.user = user;
  },
  resetUser(state) {
    state.user = false;
    state.mode = 0;
    state.stream = 1;
  },
  setCams(state, cams) {
    const _cams = {};

    cams.forEach(cam => {
      const _cam = prepareCam(cam);
      _cams[cam.id] = _cam;
    });

    debounceLimit =
      Math.round(cams.length / CAMS_COUNT_TO_INCREASE_DEBOUNCE_LIMIT) * 10 ||
      debounceLimit;

    state.cams = _cams;
  },
  changeCamState(state, data) {
    const cam = state.cams[data.id];

    if (!cam) {
      return;
    }

    debounceCount++;

    addToMap(data);

    if (debounceCount > debounceLimit) {
      this.commit("updateCams");
      return;
    }

    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    debounceTimeout = setTimeout(() => {
      this.commit("updateCams");
    }, 1000);
  },
  updateCams(state) {
    queueMap.forEach((cam, camId) => {
      const stateCam = state.cams[camId];

      stateCam.states = {
        ...stateCam.states,
        ...cam.states
      };

      stateCam.rt = {
        ...stateCam.rt,
        ...stateCam.status,
        ...cam.rt
      };

      stateCam.status.state = getStateCamFromSocket(stateCam.states);

      stateCam.icon = prepareCamStatusesIconList({
        settings: stateCam.settings,
        status: stateCam.rt,
        states: stateCam.states
      });
    });

    queueMap.clear();
    debounceCount = 0;
    clearTimeout(debounceTimeout);
  },
  setCamIdFromCell(state, camId) {
    state.camIdFromCell = camId;
  },
  setViews(state, views) {
    state.mainView = new MainView();

    state.localViews = views.localViews;

    state.globalViews = views.globalViews;
  },
  saveView(state, view) {
    let existing = state.localViews.find(v => v.id == view.id);

    if (existing) {
      Object.assign(existing, view);
      existing.changed = false;
    } else {
      state.localViews.push({
        ...view,
        changed: false,
        temp: false
      });
    }
  },
  removeView(state, viewId) {
    state.localViews = state.localViews.filter(view => view.id != viewId);
  },
  changeViewGrid(state, { grid, viewId }) {
    const view = getView(state, viewId);

    if (view) {
      view.grid = grid;
      view.changed = true;
    }
  },
  changeStream(state, stream) {
    state.stream = stream;
    sessionStorage.setItem(sessionStorageKeys.STREAM_QUALITY, stream);
  },
  addCamsIntoView(state, { camIds, viewId }) {
    const view = getView(state, viewId);

    if (view) {
      view.changed = true;

      view.cams.forEach((cam, i) => {
        if (!cam) {
          view.cams[i] = camIds.shift();
        }
      });

      view.cams = [...view.cams, ...camIds];

      if (view.cams.length > state.grids.find(g => g.value == view.grid).max) {
        let grid =
          state.grids.find(g => g.max >= view.cams.length) || last(state.grids);
        view.grid = grid.value;
      }

      state.grid = view.grid;
    }
  },
  removeCamFromView(state, { camId, viewId }) {
    const view = getView(state, viewId);

    if (camId === null) {
      view.cams = [];
      view.changed = true;
      return;
    }

    let index = view.cams.findIndex(id => id == camId);

    if (view && index > -1) {
      view.cams[index] = null;

      let last = null;

      view.cams.forEach((cam, i) => {
        if (cam) last = i + 1;
      });

      view.cams = last === null ? [] : view.cams.slice(0, last);
      view.changed = true;
    }
  },
  replaceCam(state, { camId, replacedCamId, viewId }) {
    const view = getView(state, viewId);

    let index = view.cams.findIndex(id => id == replacedCamId);
    let cams = [...view.cams];

    cams[index] = camId;
    view.cams = cams;
    view.changed = true;
  },
  resetTempView(state) {
    state.mainView.grid = state.grids[0].value;
    state.mainView.cams = [];
    state.mainView.changed = false;
  },
  setMaps(state, maps) {
    maps.forEach(map => {
      let items = [];
      forEach(map.map, (item, i) => {
        items.push(helper.transformMap(item, i, map));
      });

      map.map = items;
    });

    state.maps = maps;
  },
  updateMap(state, { mapGroupId, mapId, map }) {
    let group = state.maps.find(group => group.id == mapGroupId) || null;

    if (group) {
      let _map = group.map.find(map => map.id == mapId) || null;

      if (_map) {
        Object.assign(_map, map);
      }
    }
  },
  setMode(state, mode) {
    state.mode = mode;
    sessionStorage.setItem(sessionStorageKeys.STREAM_MODE, mode);
  },
  setGrid(state, grid) {
    state.grid = grid;
  },
  setUplodedVideos(state, video) {
    const canseledStatuses = ["ARCH_TASK_CANCELLED", "ARCH_TASK_NOT_EXISTS"];
    if (canseledStatuses.indexOf(video.status) !== -1) {
      return;
    }
    let listVideo = [...state.uploadedVideos];

    let videoState = {
      percent: 0,
      allVideoCount: 0,
      completeVideoCount: 0,
      status: "success"
    };

    let completeVideo = [];

    if (listVideo.length > 0) {
      const sameSessionVideo = listVideo.find(
        item => item.session === video.session
      );
      if (sameSessionVideo) {
        video.intervalDateVideo = sameSessionVideo.intervalDateVideo;
      }
      listVideo = listVideo.filter(item => item.session !== video.session);
    }

    listVideo.push(video);

    listVideo = listVideo.map(item => {
      item.percent = parseInt(item.percent);
      if (item.hasOwnProperty("link")) {
        if (!!item.link && !item.link.includes(location.origin)) {
          item.link = location.origin + item.link;
        }
        if (!!item.link) {
          item["statusLoad"] = "success";
          item["tooltipText"] = "video.complete";
          completeVideo.push(item);
          videoState.completeVideoCount += 1;
          videoState.percent += 100;
        }
      } else {
        item["link"] = null;
        item["statusLoad"] = !!item.problem ? "fail" : "progress";
        item["tooltipText"] = !!item.problem ? item.problem : "video.loading";
        videoState.percent += item.percent;
      }

      return item;
    });

    localStorage.setItem("uploadedVideos", JSON.stringify(completeVideo));
    state.uploadedVideos = listVideo;

    videoState.percent = videoState.percent / listVideo.length;
    videoState.allVideoCount = listVideo.length;
    videoState.status =
      videoState.completeVideoCount === videoState.allVideoCount
        ? "success"
        : listVideo.every(item => !!item.problem)
        ? "fail"
        : "progress";

    state.videoState = videoState;
  },
  deleteUplodedVideo(state, id) {
    const newUploadedVideos = [...state.uploadedVideos].filter(
      item => item.session !== id
    );
    const newCompleteVideos = newUploadedVideos.filter(item => !!item.link);
    const commonPercent = newUploadedVideos.reduce(
      (summ, video) => (!!video.link ? summ + 100 : summ + video.percent),
      0
    );

    localStorage.setItem("uploadedVideos", JSON.stringify(newCompleteVideos));

    state.uploadedVideos = newUploadedVideos;

    state.videoState = {
      allVideoCount: newUploadedVideos.length,
      percent: commonPercent / newUploadedVideos.length,
      completeVideoCount: newCompleteVideos.length,
      status:
        newCompleteVideos.length === newUploadedVideos.length
          ? "success"
          : newUploadedVideos.every(item => !!item.problem)
          ? "fail"
          : "progress"
    };
  },
  setArchCnvEnabled(state, isEnabled) {
    state.archCnvEnabled = isEnabled;
  },
  setWsWebConnect(state, wsWebConnect) {
    state.wsWebConnect = wsWebConnect;
  },
  setObjectState(state, { getters, data }) {
    const activeMap = getters.getActiveMap;
    if (activeMap) {
      const object = activeMap.objects.find(
        o => o.type === data.type && o.id === data.id
      );
      object.status = data.state;
    }
  }
};



// WEBPACK FOOTER //
// ./src/store/mutations.js